import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  Box,
  FormHelperText,
  Stack,
  Typography,
  FormControl,
  TextField,
  MenuItem,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { calculateAgeToday } from '../../Utilities/HelperMethods';
import useForm from '../../../pages/AdminContentMgt/Components/useForm';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { GetStateList, GetSuffixDropDown } from '../../../api/proposals';

dayjs.extend(utc);

const BaseInformation = forwardRef((props, ref) => {
  const [suffixLists, setSuffixLists] = useState();
  const [residentState, setResidentState] = useState();

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ('firstName' in fieldValues)
      temp.firstName = fieldValues.firstName ? '' : 'First name is required.';
    if ('lastName' in fieldValues)
      temp.lastName = fieldValues.lastName ? '' : 'Last name is required.';
    if ('annuitantFirstName' in fieldValues)
      temp.annuitantFirstName = fieldValues.annuitantFirstName
        ? ''
        : 'Annuitant first name is required.';
    if ('annuitantLastName' in fieldValues)
      temp.annuitantLastName = fieldValues.annuitantLastName
        ? ''
        : 'Annuitant last name is required.';
    if ('annuitantGender' in fieldValues)
      temp.annuitantGender = fieldValues.annuitantGender ? '' : 'Annuitant Gender is required.';
    if ('annuitantAge' in fieldValues)
      temp.annuitantAge = fieldValues.annuitantAge ? '' : 'Annuitant age is required.';
    if ('Gender' in fieldValues) temp.Gender = fieldValues.Gender ? '' : 'Gender is required.';
    if ('age' in fieldValues)
      temp.age = fieldValues.age && +fieldValues.age > 0 ? '' : 'Invalid age.';
    if ('residentState' in fieldValues)
      temp.residentState = fieldValues.residentState ? '' : 'Resident state is required.';
    setErrors({
      ...temp,
    });

    if (fieldValues == values) return Object.values(temp).every((x) => x == '');
  };

  const {
    values,
    errors,
    setErrors,
    handleInputChange,
    handleIntChange,
    handleDataChange,
    handleNoEventDateChange,
    handleDateChange,
  } = useForm(
    {
      firstName: '',
      lastName: '',
      annuitantFirstName: '',
      annuitantLastName: '',
      annuitantGender: '',
      annuitantAge: '',
      Gender: 'M',
      suffix: '',
      effectiveDate: new Date().toISOString(),
      dateOfBirth: new Date().toISOString(),
      age: '',
      residentState: '',
      deleted: false,
    },
    true,
    validate
  );

  useEffect(() => {
    if (values.dateOfBirth !== '') {
      handleDataChange('age', calculateAgeToday(values.dateOfBirth));
    }
  }, [values.dateOfBirth]);

  const getClientInfo = () => {
    if (validate()) {
      return values;
    }
  };

  useImperativeHandle(ref, () => {
    return {
      getClientInfo: () => getClientInfo(),
    };
  }, [values, errors]);

  const SuffixList = useQuery({
    queryKey: ['Suffix'],
    queryFn: async () => await GetSuffixDropDown()(),
  });

  const stateList = useQuery({
    queryKey: ['GetStateList'],
    queryFn: async () => await GetStateList()(),
  });

  useEffect(() => {
    if (SuffixList.isSuccess && SuffixList.data != null) {
      setSuffixLists(SuffixList.data);
    }
  }, [SuffixList.isSuccess, SuffixList.data]);

  useEffect(() => {
    if (stateList.isSuccess && stateList.data != null) {
      setResidentState(stateList.data);
    }
  }, [stateList.isSuccess, stateList.data]);

  return (
    <Stack spacing="1em">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <TextField
          name="firstName"
          label="First Name *"
          size="small"
          value={values.firstName}
          onChange={handleInputChange}
          error={errors.firstName !== undefined && errors.firstName !== ''}
          fullWidth
          variant="standard"
          helperText={errors.firstName}
        />
        <TextField
          name="lastName"
          label="Last Name *"
          size="small"
          value={values.lastName}
          onChange={handleInputChange}
          error={errors.lastName !== undefined && errors.lastName !== ''}
          helperText={errors.lastName}
          variant="standard"
          fullWidth
        />
      </Box>
      <Box display="flex" alignItems="center" gap="10px">
        <FormControl fullWidth>
          <TextField
            id="form-suffix"
            name="suffix"
            size="small"
            value={values.suffix}
            variant="standard"
            label="Suffix"
            select={true}
            onChange={handleInputChange}
          >
            <MenuItem value="">
              <em>Select a suffix</em>
            </MenuItem>
            {suffixLists?.map((suff, index) => (
              <MenuItem value={suff.Value} key={`${suff.Text}-${index}`}>
                {suff.Text}
              </MenuItem>
            ))}
          </TextField>
          <FormHelperText>{errors.suffix}</FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <TextField
            label="Gender"
            size="small"
            variant="standard"
            select={true}
            name="Gender"
            value={values.Gender}
            onChange={handleInputChange}
            error={errors.Gender !== undefined && errors.Gender !== ''}
          >
            <MenuItem value="">
              <em>Select a Gender</em>
            </MenuItem>
            <MenuItem value="M">Male</MenuItem>
            <MenuItem value="F">Female</MenuItem>
          </TextField>
          <FormHelperText>{errors.suffix}</FormHelperText>
        </FormControl>
      </Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date of Birth"
              name="dateOfBirth"
              value={values.dateOfBirth ? dayjs.utc(values.dateOfBirth) : null}
              size="small"
              variant="standard"
              onChange={(newVal) => {
                if (newVal && dayjs(newVal).isValid()) {
                  const isoUTC = dayjs(newVal).utc().toISOString();
                  handleNoEventDateChange(isoUTC, 'dateOfBirth');
                } else {
                  handleNoEventDateChange(null, 'dateOfBirth');
                }
              }}
              slotProps={{
                textField: { variant: 'standard' },
              }}
            />
          </LocalizationProvider>
          <FormHelperText className="color-error">
            {errors.age !== undefined && errors.age !== '' && values.age <= 0
              ? 'Date of birth is required'
              : ' '}
          </FormHelperText>
        </Box>
        <TextField
          disabled
          fullWidth
          id="outlined-required"
          label="Age"
          variant="standard"
          size="small"
          name="age"
          value={values.age}
          onChange={handleInputChange}
          error={errors.age !== undefined && errors.age !== '' && values.age <= 0}
          helperText={
            errors.age !== undefined && errors.age !== '' && values.age <= 0 ? errors.age : ' '
          }
          InputProps={{ endAdornment: <InputAdornment position="end"> Yrs </InputAdornment> }}
        />
      </Stack>
      <Stack>
        <FormControl fullWidth>
          <TextField
            label="Resident State"
            name="residentState"
            size="small"
            variant="standard"
            select={true}
            value={values.residentState}
            onChange={handleInputChange}
            error={errors.residentState !== undefined && errors.residentState !== ''}
          >
            <MenuItem value="">Resident State</MenuItem>
            {residentState?.states
              ?.filter((state) => state.Value !== 'NY')
              ?.map((st, index) => (
                <MenuItem value={st.Value} key={`${st.Key}-${index}`}>
                  {st.Key}
                </MenuItem>
              ))}
          </TextField>
          <FormHelperText className="color-error">{errors.residentState}</FormHelperText>
        </FormControl>
      </Stack>
      <Stack direction={'column'} spacing={2}>
        <Typography fontSize="20px" mt={1}>
          Annuitant Information
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TextField
            name="annuitantFirstName"
            variant="standard"
            size="small"
            label="Annuitant First Name *"
            value={values.annuitantFirstName}
            onChange={handleInputChange}
            error={errors.annuitantFirstName !== undefined && errors.annuitantFirstName !== ''}
            fullWidth
            helperText={errors.annuitantFirstName}
          />
          <TextField
            name="annuitantLastName"
            label="Annuitant Last Name *"
            variant="standard"
            size="small"
            value={values.annuitantLastName}
            onChange={handleInputChange}
            error={errors.annuitantLastName !== undefined && errors.annuitantLastName !== ''}
            fullWidth
            helperText={errors.annuitantLastName}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TextField
            name="annuitantAge"
            variant="standard"
            label="Annuitant Age *"
            size="small"
            value={values.annuitantAge}
            onChange={handleInputChange}
            error={errors.annuitantAge !== undefined && errors.annuitantAge !== ''}
            fullWidth
            helperText={errors.annuitantAge}
          />
          <FormControl fullWidth>
            <TextField
              label="Annuitant Gender"
              name="annuitantGender"
              variant="standard"
              size="small"
              select={true}
              value={values.annuitantGender}
              onChange={handleInputChange}
              error={errors.annuitantGender !== undefined && errors.annuitantGender !== ''}
            >
              <MenuItem value="">Annuitant Gender</MenuItem>
              <MenuItem value="M">Male</MenuItem>
              <MenuItem value="F">Female</MenuItem>
            </TextField>
            <FormHelperText className="color-error">{errors.annuitantGender}</FormHelperText>
          </FormControl>
        </Box>
      </Stack>
    </Stack>
  );
});

export default BaseInformation;
